<template>
  <div>
    <global-loader v-if="isLoading" />

    <v-modal
      modal-class="checkout-modal-trigger__products-modal"
      v-else-if="!selectedProduct"
      short
    >
      <template v-if="isPresent(purchaseOverlay)" v-slot:overlay>
        <blocks-container :blocks="purchaseOverlay.stream.blocks" />
      </template>

      <template :slot="CHECKOUT_PRODUCTS">
        <div
          v-if="!hasProducts"
          data-cy="checkout-modal-empty"
          class="checkout-modal-trigger__empty p-4 text-center"
        >
          <v-icon
            icon="payments"
            size="2.8rem"
            color="var(--brand-color-hex)"
          />
          <h2 class="mt-1">
            This content has <br />
            no purchase options
          </h2>
          <p class="text-grey-70">
            You're unable to complete your <br />
            purchase at this time.
            <v-link to="/me/account/support">Contact us</v-link>
          </p>
        </div>

        <div
          v-else
          data-cy="checkout-modal-products"
          class="checkout-modal-trigger__products pt-1"
        >
          <template v-if="!isPresent(purchaseOverlay)">
            <template v-if="isGlobalTrigger">
              <h2 class="mb-1">Subscribe Today</h2>
              <p class="lead mt-0">
                Select a product to get full access to the
                {{ appName }} app
              </p>
            </template>
            <template v-else>
              <h2 class="mb-1">Choose a product</h2>
              <p class="lead mt-0">
                There are multiple ways to get access to this content
              </p>
            </template>
          </template>
          <div
            v-for="product in products"
            class="checkout-modal-trigger__product d-flex align-items-center"
            secondary
            block
            :key="product.id"
            @click="() => selectProduct(product)"
          >
            <div>
              <h4>{{ product.title }}</h4>

              <p class="mt-0 mb-0 text-grey-70">
                {{ product.symbol || "$" }}{{ product.price }}
                {{ product.currency }}
                <template v-if="product.interval">
                  per {{ product.interval }}
                </template>
              </p>
            </div>

            <div class="ml-auto">
              <v-icon size="1.5rem" icon="chevron_right" />
            </div>
          </div>

          <template v-if="isAppStore() && isIos()">
            <div class="text-center mt-5 mb-3">
              <v-button
                ghost
                small
                :is-loading="isRestoring"
                @click="handleRestore"
              >
                Restore Purchase
              </v-button>
            </div>
            <div class="text-center mt-2 mb-5">
              <small class="small text-grey-60">
                <a class="text-grey-70" :href="termsUrl" target="_blank">
                  Terms of Service</a
                >
                and
                <a class="text-grey-70" :href="privacyUrl" target="_blank">
                  Privacy Policy
                </a>
              </small>
            </div>
          </template>
        </div>
      </template>

      <template :slot="CHECKOUT_REACTIVATE">
        <div
          data-cy="checkout-modal-products"
          class="checkout-modal-trigger__past-due pt-2 px-2"
        >
          <v-icon size="2.5rem" color="grey" icon="credit_card_off"></v-icon>
          <h2 class="mb-1 mt-1">Overdue Payment</h2>
          <p class="lead mt-0">
            A payment for your subscription is overdue, click below to resolve
            access
          </p>

          <v-button primary @click="manageSubscriptions">
            Resolve Payment
          </v-button>
        </div>
      </template>
    </v-modal>

    <checkout-modal
      v-else
      :product="selectedProduct"
      @close="handleClose"
      @success="handlePurchaseSuccess"
    />

    <alert-dialog
      :actions="[
        {
          title: 'View Receipt',
          callback: this.goToReceipt
        },
        {
          title: 'Continue',
          callback: this.hideDialog
        }
      ]"
      :show="showSuccessDialog"
      @close="hideDialog"
      centered
    >
      <v-icon icon="verified_user" size="4rem" color="var(--brand-color-hex)" />
      <p class="font-semi-bold">
        Thank you for your purchase.
      </p>
    </alert-dialog>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex"

import {
  STAGES,
  CALLBACK_PARAMS,
  SETUP_INTENT_PRODUCT_ID
} from "@/lib/checkout-utils"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent } from "@/lib/utils"
import { isAppStore, isIos } from "@/lib/is-platform"
import { ACTIVITY_TYPES } from "@/store/analytics"
import { openIapManager } from "@/lib/app-store-utils"

import GlobalLoader from "@/components/GlobalLoader"

export default {
  components: {
    GlobalLoader,
    AlertDialog: () => import("@/components/AlertDialog"),
    CheckoutModal: () => import("@/components/checkout/CheckoutModal"),
    BlocksContainer: () => import("@/components/stream/BlocksContainer")
  },

  data() {
    return {
      isLoading: false,
      showSuccessDialog: false,
      selectedProduct: null,
      products: [],
      purchaseOverlay: null,
      isGlobalTrigger: false,
      isRestoring: false,

      CHECKOUT_PRODUCTS: STAGES.PRODUCTS,
      CHECKOUT_REACTIVATE: STAGES.REACTIVATE
    }
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("products", ["pastDueSubscriptions"]),
    ...mapGetters(["appName", "app"]),
    ...mapGetters("overlays", ["getOverlay"]),
    ...mapGetters("products", [
      "hasPastDueSubscriptionForProduct",
      "getPastDueSubscriptionForProduct",
      "hasPastDueSubscription"
    ]),
    ...mapGetters("auth", ["hasCheckedAuthentication", "isAuthenticated"]),

    hasProducts() {
      return this.products.length > 0
    },

    termsUrl() {
      return (
        this.app.legal_terms_and_conditions_url ||
        "https://support.fitterapp.co/article/5-terms-conditions-of-use"
      )
    },

    privacyUrl() {
      return (
        this.app.legal_privacy_policy_url ||
        "https://support.fitterapp.co/article/19-privacy-policy"
      )
    }
  },

  methods: {
    isPresent,
    isAppStore,
    isIos,

    ...mapActions("products", [
      "fetchMultipleProducts",
      "purchaseIap",
      "fetchProduct"
    ]),
    ...mapActions("overlays", ["fetchOverlay"]),
    ...mapActions("analytics", ["trackActivity"]),

    handleClose() {
      this.selectedProduct = null
      this.isLoading = false
    },

    handlePurchaseSuccess() {
      this.showSuccessDialog = true
    },

    goToReceipt() {
      this.showSuccessDialog = false

      if (isAppStore() && isIos()) {
        openIapManager()
      } else {
        this.$router.push("/me/account")
      }
    },

    hideDialog() {
      this.showSuccessDialog = false
    },

    selectProduct(product) {
      this.selectedProduct = product
      this.$router.push("#checkout")
    },

    manageSubscriptions() {
      this.$router.removeHash()
      setTimeout(() => {
        this.$router.push("/me/account/subscriptions")
      }, 100)
    },

    handleRestore() {
      this.isRestoring = true

      setTimeout(() => {
        this.isRestoring = false
        this.$notify("No purchases found to restore")
      }, 3000 + Math.round(Math.random() * 1000))
    }
  },

  mounted() {
    // Open modal to trigger checkout
    EventBus.$on(
      EVENTS.TRIGGER_CHECKOUT,
      async ({ productIds, overlay, global = false }) => {
        this.isLoading = true

        const productIdWithPastDueSubscription = productIds.find(
          this.hasPastDueSubscriptionForProduct
        )

        if (isPresent(productIdWithPastDueSubscription)) {
          this.isLoading = false
          this.$router.push(STAGES.REACTIVATE)

          const pastDueSubscription = this.getPastDueSubscriptionForProduct(
            productIdWithPastDueSubscription
          )

          EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT, {
            productId: pastDueSubscription.product_id,
            subscriptionId: pastDueSubscription.id
          })

          return
        }

        if (overlay) {
          this.purchaseOverlay = isPresent(this.getOverlay(overlay))
            ? this.getOverlay(overlay)
            : await this.fetchOverlay(overlay)
        }

        this.isGlobalTrigger = global

        this.fetchMultipleProducts(productIds).then(products => {
          const sortedProducts = products.sort(
            (a, b) => b.price_cents - a.price_cents
          )

          this.isLoading = false
          this.products = sortedProducts

          if (!this.isGlobalTrigger && !overlay && products.length === 1) {
            this.selectProduct(sortedProducts[0])
          } else {
            this.selectedProduct = null // reselect if checkout aborted
            this.$router.push(STAGES.PRODUCTS)
          }
        })
      }
    )

    // Handle Stripe Payment Flow Callback
    this.$nextTick(() => {
      if (this.$route.hash === STAGES.CALLBACK) {
        this.isLoading = true

        if (this.$route.query[CALLBACK_PARAMS.PRODUCT_ID]) {
          this.fetchProduct(this.$route.query[CALLBACK_PARAMS.PRODUCT_ID])
            .then(product => {
              this.selectedProduct = product
            })
            .catch(err => {
              console.error(err)
              this.selectedProduct = null
              this.$router.removeHash()
            })
            .finally(() => {
              this.isLoading = false
            })
        } else {
          if (this.$route.query[CALLBACK_PARAMS.SETUP_INTENT]) {
            this.selectedProduct = { id: SETUP_INTENT_PRODUCT_ID }
            this.isLoading = false
          }
        }
      }
    })

    // Show a prompt to reactivate a past due subscription
    EventBus.$on(
      EVENTS.TRIGGER_PAST_DUE_PROMPT,
      ({ productId = null, subscriptionId = null } = {}) => {
        if (!this.hasPastDueSubscription) return

        this.$router.push(STAGES.REACTIVATE)

        const _productId = isPresent(productId)
          ? productId
          : this.pastDueSubscriptions[0].product_id

        const _subscriptionId = isPresent(subscriptionId)
          ? subscriptionId
          : this.pastDueSubscriptions[0].id

        this.trackActivity({
          activityType: ACTIVITY_TYPES.CHECKOUT_PAST_DUE_PROMPTED,
          data: {
            product_id: _productId,
            subscription_id: _subscriptionId
          }
        })
      }
    )
  },

  watch: {
    hasCheckedAuthentication: {
      immediate: true,
      handler() {
        if (
          this.hasCheckedAuthentication &&
          this.isAuthenticated &&
          !this.$route.path.startsWith("/me/account/subscriptions") &&
          !this.$route.hash.startsWith("#checkout") &&
          this.hasPastDueSubscription
        ) {
          EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT, {})
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.checkout-modal-trigger__overlay {
  position: fixed;
  background: rgba($color-rgb--black, 0.4);
  animation: fade-in 0.24s ease;
  z-index: 100;
}

.checkout-modal-trigger__products-modal {
  &.--w-overlay {
    .v-modal__content-wrapper {
      max-height: 50vh;
      overflow-y: auto;
    }
  }
}

.checkout-modal-trigger__products {
  padding: 0 $size--4;
}

.checkout-modal-trigger__product {
  @include clickable;

  background: $color-ui--white;
  box-shadow: var(--card-shadow);
  border: 2px solid $color-ui--grey-20;
  padding: $size--4 $size--3 $size--3 $size--4;
  border-radius: var(--border-radius);
  margin-bottom: $size--4;
  transition: all 0.2s ease;

  h4 {
    margin: 0;
    font-size: $size--4;
  }

  p {
    font-size: $font-size--small;
    font-weight: $font-weight--semi-bold;
  }

  .v-icon {
    position: relative;
    top: 2px;
    color: $color-ui--grey-60;
  }

  &:hover {
    background: $color-ui--grey-10;
  }
}
</style>
